import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './GameConfirmationModal.css';
import { startGame } from '../../services/gameActionService';
import { useGameDetails } from '../../hooks/useUserGames';

interface GameHistoryItem {
  action: string;
  response: {
    status: string;
    reaction: string;
    feedback: string;
    next_prompt: string;
    score_adjustment: number;
    suggestions: string[];
    response_quality: string;
  };
  timestamp: string;
  score_change: number;
}

interface GameConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  gameTitle: string;
  gameKey: string;
  description: string;
  difficulty: string;
  isResume?: boolean;
  sessionId?: string;
  lastPrompt?: string;
  videoUrl?: string;
  imageUrl?: string;
  backgroundImageUrl?: string;
}

const GameConfirmationModal: React.FC<GameConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  gameTitle,
  gameKey,
  description,
  difficulty,
  isResume = false,
  sessionId,
  lastPrompt,
  videoUrl,
  imageUrl,
  backgroundImageUrl
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { game, error: gameError } = useGameDetails(sessionId || null);

  const difficultyColor = useMemo(() => {
    switch (difficulty.toLowerCase()) {
      case 'easy':
        return '#28a745';
      case 'medium':
        return '#ffc107';
      case 'hard':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  }, [difficulty]);

  if (!isOpen) return null;

  const handleStartGame = async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      if (isResume && game) {
        // For resume, match the state structure of a new game
        onClose();
        onConfirm();
        
        // Get the last suggestions from history if available
        const lastSuggestions = game.history && game.history.length > 0
          ? game.history[game.history.length - 1].response.suggestions
          : [];

        navigate(`/game/${gameKey}`, {
          replace: true,
          state: {
            sessionId: game.id,
            gameType: game.game_type,
            difficulty: game.difficulty,
            initialScenario: game.current_prompt,
            initialScore: game.score,
            status: game.status,
            reaction: game.reaction,
            initialSuggestions: lastSuggestions,
            history: game.history, // Pass the complete history for context
            background_image_url: backgroundImageUrl
          }
        });
      } else {
        // For new game, call the start API
        const gameState = await startGame(gameKey, difficulty.toLowerCase());
        
        if (!gameState || !gameState.session_id || !gameState.scenario) {
          throw new Error('Invalid game state received from server');
        }

        onClose();
        onConfirm();
        
        navigate(`/game/${gameKey}`, {
          replace: true,
          state: {
            sessionId: gameState.session_id,
            gameType: gameState.game_type,
            initialScenario: gameState.scenario,
            initialSuggestions: gameState.suggestions || [],
            initialScore: gameState.score || 0,
            difficulty: difficulty.toLowerCase(),
            status: gameState.status,
            reaction: gameState.reaction,
            history: [], // Initialize with empty history for new games
            background_image_url: backgroundImageUrl
          }
        });
      }
    } catch (error) {
      console.error('Error with game:', error);
      setError(error instanceof Error ? error.message : 'Failed to handle game request. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {videoUrl ? (
          <video
            className="modal-background-video"
            autoPlay
            loop
            muted
            playsInline
            poster={imageUrl}
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/webm" />
          </video>
        ) : (
          imageUrl && <img src={imageUrl} alt={gameTitle} className="modal-background-image" />
        )}
        <div className="modal-inner-content">
          <h2>{isResume ? `Resume ${gameTitle}` : gameTitle}</h2>
          
          {isResume && lastPrompt && (
            <div className="resume-info">
              <h3>Current Game Status</h3>
              {isLoading && <div className="loading">Loading game details...</div>}
              {gameError && <div className="error">{gameError}</div>}
              {game && (
                <>
                  <div className="compact-game-info">
                    <div className="info-row">
                      <span className="game-type">{gameTitle}</span>
                      <span className="game-score">{game.score}<i className="lni lni-star"></i></span>
                    </div>
                    <div className="info-row">
                      <div className="game-meta">
                        <span className="difficulty">{game.difficulty}</span>
                        <span className="status">{game.status}</span>
                      </div>
                      <span className="actions-count">{game.history?.length || 0} actions</span>
                    </div>
                  </div>
                  <p className="last-prompt">{lastPrompt}</p>
                  {game.history && game.history.length > 0 && (
                    <div className="history-summary">
                      <h4>Recent Actions</h4>
                      <ul>
                        {game.history.slice(-3).map((item: GameHistoryItem, index: number) => (
                          <li key={index}>
                            <span className="action">{item.action}</span>
                            <span className="response">{item.response.feedback}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {!isResume && (
            <>
              <p className="game-description">{description}</p>
              <p className="difficulty-info" style={{ color: difficultyColor }}>
                Selected Difficulty: {difficulty}
              </p>
            </>
          )}
          
          {error && (
            <div className="error-message">
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => {
                  setError(null);
                  handleStartGame();
                }}
                disabled={isLoading}
              >
                Retry
              </button>
            </div>
          )}

          {!isResume && (
            <div className="warning-box">
              <h3>⚠️ Warning</h3>
              <p>This is an AI-generated game experience. We are not responsible for any psychological or emotional effects that may arise from playing this game. Please proceed with caution.</p>
            </div>
          )}
          
          <div className="modal-buttons">
            <button 
              className="cancel-btn" 
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button 
              className={`confirm-btn ${isLoading ? 'loading' : ''}`}
              onClick={handleStartGame}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  {isResume ? 'Resuming...' : 'Starting...'} 
                  <span className="spinner"></span>
                </>
              ) : (
                isResume ? 'Resume Game' : 'Start Game'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameConfirmationModal; 